import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { captureApiException } from '@/utility/sentry'

const bitrixApi = import.meta.env.VITE_BITRIXAPI

export const useSupplierStore = defineStore('supplier', () => {
    // Data states
    const badges = ref([])
    const company = ref([])
    const pkg = ref([])
    const primaryIndustry = ref([])
    const prisen = ref([])
    const prisenTest = ref([])
    const isAffiliate = ref(null)
    const loading = ref(true)
    const dataLoaded = ref(false)
    const coverUrl = ref(null)
    const bitrixPackage = ref(null)
    const jobDescriptions = ref(null)

    // Toggle states that gives importance to have their own state
    const kanIkke = ref(null)
    const multipleOffers = ref(null)
    const newsfeedV2 = ref(false)
    const newsfeedV3 = ref(true)
    const marketplaceV2 = ref(null)
    const showEditOffers = ref(null)
    const tinderMarketplace = ref(null)
    const notPaid = ref(null)
    const hasWatchedVideo = ref(null)
    const showDeadlineOffers = ref(null)
    const paysTaxSelection = ref(null)
    const dynamicPopup = ref(null)
    const trackingPhoneEmail = ref(null)
    const fullblacklisted = ref(null)
    const semiblacklisted = ref(null)

    // important states for numbering
    const nummode = ref(0)
    const maxreserve_size = ref(0)

    const bitrixPackageLoaded = ref(false)
    const bitrixPackageInFlight = ref(null)

    // accepted params: company, package, prisen, prisen_test, badges, primaryIndustry
    async function fetchAll (type = ['company', 'package', 'prisen', 'prisen_test', 'badges', 'primaryIndustry']) {
        const haaid = localStorage.getItem('haaid')

        try {
            const response = await axios.get(bitrixApi + 'supplier/GetSupplierProfileByIdV2/' + haaid + '?data=' + type.join(','), {
                headers: {
                    'Content-Type': 'application/json',
                    'x-localization': 'da'
                }
            })

            const res = response.data.data

            if (res.badges) {
                badges.value = Array.isArray(res.badges) ? res.badges : []
            }

            if (res.package) {
                pkg.value = res.package || {}
                notPaid.value = pkg.value.marketplace_notpaid
                hasWatchedVideo.value = pkg.value.play_marketplace_video
                nummode.value = Number(pkg.value.nummode)
                notPaid.value = pkg.value.marketplace_notpaid
                semiblacklisted.value = pkg.value?.SEMI_BLACKLIST == 1
            }
            
            if (res.company) {
                company.value = res.company || {}
                dynamicPopup.value = company.value?.UF_CRM_COMPANY_DYNAMIC_POPUP == 1
                maxreserve_size.value = Number(company.value?.UF_CRM_COMPANY_RESERVE_SIZE)
                paysTaxSelection.value = Number(company.value?.UF_CRM_COMPANY_ENABLE_WHO_PAYS_THE_FEE) == 1
                isAffiliate.value = company.value?.UF_CRM_COMPANY_PACKAGE === 'affiliatepartner'
                newsfeedV3.value = company.value?.UF_CRM_COMPANY_NEWSFEED_V3
                kanIkke.value = company.value?.UF_CRM_COMPANY_KANIKKE_TILBUD
                marketplaceV2.value = Number(company.value?.UF_CRM_COMPANY_MARKETPLACE_V2) === 1
                showDeadlineOffers.value = company.value?.UF_CRM_COMPANY_DEADLINE_OFFER == 1
                showEditOffers.value = company.value?.UF_CRM_COMPANY_ENABLE_EDIT_OFFER
                tinderMarketplace.value = Number(company.value?.UF_CRM_COMPANY_TINDER_SOLUTION) == 1
                multipleOffers.value = company.value?.UF_CRM_COMPANY_MULTI_OFFERS
                fullblacklisted.value = company.value?.UF_CRM_COMPANY_BLACKLIST_DONE == 1
                jobDescriptions.value = company.value?.UF_CRM_COMPANY_JOB_PREDICTION

                nummode.value = Number(pkg.value.nummode)
                maxreserve_size.value = Number(company.value?.UF_CRM_COMPANY_RESERVE_SIZE)

                if (company.value?.UF_CRM_COMPANY_START_DATE)
                    localStorage.setItem('start_date', company.value.UF_CRM_COMPANY_START_DATE)
            }

            if (res.primaryIndustry) {

            }

            if (res.prisen) {
                prisen.value = res.prisen || {}
            }

            if (res.prisenTest) {
                prisenTest.value = res.prisen_test || {}
            }

            dataLoaded.value = true

        } catch (error) {
            captureApiException(error, 'supplier.js', 'fetchAll')
            console.error(error)
            return false
        }
    }

    // accepted params: company, package, prisen, prisen_test, badges, primaryIndustry
    async function getSupplier (type = []) {
        const haaid = localStorage.getItem('haaid')

        try {
            const response = await axios.get(`${bitrixApi}supplier/GetSupplierProfileByIdV2/${haaid}?data=${type.join(',')}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'x-localization': 'da'
                }
            })
            const res = response.data.data

            if (res.package) {
                pkg.value = res.package || {}
                notPaid.value = pkg.value.marketplace_notpaid
                hasWatchedVideo.value = pkg.value.play_marketplace_video
                nummode.value = Number(pkg.value.nummode)
            }
            
            if (res.company) {
                company.value = data.company || {}
                notPaid.value = pkg.value?.marketplace_notpaid
                dynamicPopup.value = company.value?.UF_CRM_COMPANY_DYNAMIC_POPUP
                maxreserve_size.value = Number(company.value?.UF_CRM_COMPANY_RESERVE_SIZE)
                paysTaxSelection.value = Number(company.value?.UF_CRM_COMPANY_ENABLE_WHO_PAYS_THE_FEE) == 1
                trackingPhoneEmail.value = company.value?.UF_CRM_COMPANY_TRACKING_PHONE_EMAIL == 1
            }

        } catch (error) {
            captureApiException(error, 'supplier.js', 'getSupplier')
            console.error(error)
            throw error
        }
    }

    async function fetchCompany (reset = false) {
        if (company.value && Object.keys(company.value).length > 0 && !reset)
            return company.value

        await fetchAll()
        return company.value
    }

    async function fetchPackage (reset = false) {
        if (pkg.value && Object.keys(pkg.value).length > 0 && !reset)
            return pkg.value

        await fetchAll()
        return pkg.value
    }

    async function fetchPrisen (reset = false) {
        if (prisen.value && Object.keys(prisen.value).length > 0 && !reset)
            return prisen.value

        await fetchAll()
        return prisen.value
    }

    async function fetchCoverUrl (reset = false) {
        const haaid = localStorage.getItem('haaid')

        if (dataLoaded.value && !reset)
            return false

        try {
            const response = await axios.get(bitrixApi + 'supplier/GetSupplierProfileById/' + haaid, {
                headers: {
                    'Content-Type': 'application/json',
                    'x-localization': 'da'
                }
            })

            const res = response.data
            const coverUrlList = res.data.CoverUrl
            const primaryIndustry = res.data.GetPrimaryIndustryV2

            if (coverUrlList[0]?.ORIGINAL_NAME)
                coverUrl.value = coverUrlList[0].ORIGINAL_NAME
            else
                coverUrl.value = primaryIndustry.PICTURE_URL

            const badgesListmerge = res.data.mergedBadges
            const badgesList = res.data.c_prisen_result

            const badgeids = []

            if (badgesListmerge.length > 0) {
                badgesListmerge.forEach(function (property, index) {
                    badgeids.push(index + 1)
                })
                badgesList.forEach(function (property, index) {
                    badgeids.push(badgeids[badgeids.length - 1] + 1)
                })
            } else {
                badgesList.forEach(function (property, index) {
                    badgeids.push(index + 1)
                })
            }

            badges.value = badgeids

        } catch (error) {
            captureApiException(error, 'supplier.js', 'fetchCoverUrl')
            console.error(error)
        }
    }

    async function fetchTaskProfile (reset = false) {
        const haaid = localStorage.getItem('haaid')

        if (dataLoaded.value && !reset)
            return false

        try {
            await axios.get(bitrixApi + 'supplier/GetSupplierProfileById/' + haaid, {
                headers: {
                    'Content-Type': 'application/json',
                    'x-localization': 'da'
                }
            })
        } catch (error) {
            captureApiException(error, 'supplier.js', 'fetchTaskProfile')
            console.error(error)
        }
    }

    async function fetchBitrixPackage (reset = false) {
        const haaid = localStorage.getItem('haaid')

        if (bitrixPackageLoaded.value && !reset)
            return bitrixPackage.value

        if (bitrixPackageInFlight.value && !reset)
            return bitrixPackageInFlight.value

        const request = axios
            .get(bitrixApi + 'bitrix/getPackage/' + haaid)
            .then((response) => {
                const resp = response?.data

                // Normalize shape: some endpoints return { success, data: {...} }
                // while others may return the data object directly. Ensure
                // callers always receive { success, data } for consistency.
                if (resp && typeof resp === 'object' && resp.success !== undefined && resp.data !== undefined) {
                    bitrixPackage.value = resp
                } else {
                    bitrixPackage.value = { success: true, data: resp }
                }

                bitrixPackageLoaded.value = true
                return bitrixPackage.value
            })
            .catch((error) => {
                captureApiException(error, 'supplier.js', 'fetchBitrixPackage')
                throw error
            })
            .finally(() => {
                bitrixPackageInFlight.value = null
            })

        bitrixPackageInFlight.value = request
        return request
    }

    const resetSupplier = () => {
        company.value = []
        pkg.value = []
        prisen.value = []
        prisenTest.value = []
        isAffiliate.value = null
        loading.value = true
        dataLoaded.value = false
        badges.value = []
        coverUrl.value = null
        multipleOffers.value = null
        newsfeedV3.value = false
        marketplaceV2.value = null
        showEditOffers.value = null
        tinderMarketplace.value = null
        bitrixPackage.value = null
        bitrixPackageLoaded.value = false
        bitrixPackageInFlight.value = null
    }

    return {
        company, pkg, prisen, prisenTest, isAffiliate, bitrixPackage, badges, coverUrl, jobDescriptions,
        kanIkke, newsfeedV3, marketplaceV2, tinderMarketplace, notPaid, hasWatchedVideo, showDeadlineOffers, multipleOffers, showEditOffers,
        nummode, maxreserve_size, paysTaxSelection, dynamicPopup, trackingPhoneEmail, fullblacklisted, semiblacklisted, 
        loading, dataLoaded,
        fetchAll, getSupplier, fetchCompany, fetchPackage, fetchPrisen, fetchCoverUrl, fetchTaskProfile, fetchBitrixPackage, resetSupplier
    }
})